import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import PosterRow from "../components/PosterRow";
import IndieTeaser from "../components/IndieTeaser";
import PodcastSpotlight from "../components/PodcastSpotlight";
import GenreChipRow from "../components/GenreChipRow";
import Disclaimer from "../components/Disclaimer";
import ServiceBadges from "../components/ServiceBadges";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function GuideHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teaser, setTeaser] = useState(false);
  const [indieItems, setIndieItems] = useState([]);
  const [followItems, setFollowItems] = useState([]);
  const [lovedItems, setLovedItems] = useState([]);
  const [cwItems, setCwItems] = useState([]);
  const [myList, setMyList] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/catalog/guide").then(({ data }) => {
      if (!alive) return;
      setChannels((data.channels || []).filter((c) => c.items?.length));
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    api.get("/catalog/home").then(({ data }) => {
      if (!alive) return;
      setTeaser(!!data.indie_teaser);
      setIndieItems((data.rows || []).find((r) => r.key === "indie_spotlight")?.items || []);
      setCwItems((data.rows || []).find((r) => r.key === "continue_watching")?.items || []);
    }).catch(() => {});
    api.get("/catalog/following-feed").then(({ data }) => { if (alive) setFollowItems(data.items || []); }).catch(() => {});
    api.get("/catalog/loved-by-friends").then(({ data }) => { if (alive) setLovedItems(data.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // My List channel strip: updates live, hides at zero.
  const wlKey = (user?.watchlist || []).join(",");
  useEffect(() => {
    let alive = true;
    const ids = user?.watchlist || [];
    if (!ids.length) { setMyList([]); return; }
    let tries = 0;
    const run = () => api.get("/watchlist").then(({ data }) => {
      if (!alive) return;
      const items = data.items || [];
      setMyList(items);
      if (items.length !== ids.length && tries < 3) { tries++; setTimeout(run, 400); }
    }).catch(() => {});
    run();
    return () => { alive = false; };
  }, [wlKey]);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="px-4 lg:px-14 py-6" data-testid="guide-home" data-home-variant="guide">
        <div className="mb-3 flex items-center gap-3">
          <span className="bg-[var(--accent)] px-3 py-1 font-head text-2xl tracking-wide text-[var(--accent-contrast)]">GUIDE</span>
          <span className="text-xs uppercase tracking-widest text-[var(--text-secondary)]">Tap any title for where to watch · Channels 101–108</span>
        </div>

        <GuideStrip title="Continue Watching" items={cwItems} navigate={navigate} progress testid="guide-continue-watching" />
        <GuideStrip title="My List" items={myList} navigate={navigate} testid="guide-my-list" className="mb-2" />
        <GenreChipRow className="mb-4" />

        {/* Cable program guide: one auto-rolling row per provider, compact title cells */}
        <section data-testid="guide-cable" className="guide-cable-wrap">
          {loading && !channels.length ? (
            <p className="p-6 text-center text-sm uppercase tracking-widest text-[var(--text-muted)]" data-testid="guide-loading">Tuning channels…</p>
          ) : (
            <div className="border border-[var(--border-subtle)] bg-[var(--border-subtle)]">
              {/* header */}
              <div className="flex gap-px bg-[var(--border-subtle)]" data-testid="guide-header-row">
                <div className="guide-ch-cell flex-none bg-[var(--bg-surface)] p-2 text-[11px] font-bold uppercase tracking-widest text-[var(--text-muted)]">Channel</div>
                <div className="flex-1 bg-[var(--bg-surface)] p-2 text-center text-[11px] font-bold uppercase tracking-[0.3em] text-[var(--accent-hover)]">★ Trending Now — Surf the Channels ★</div>
              </div>
              {/* channel rows */}
              {channels.map((ch) => {
                const dur = `${Math.max(ch.items.length * 3, 30)}s`;
                return (
                  <div key={ch.service_id} className="mt-px flex gap-px bg-[var(--border-subtle)]" data-testid={`guide-channel-${ch.service_id}`}>
                    <div className="guide-ch-cell flex flex-none flex-col justify-center p-2" style={{ background: ch.color, color: ch.text }}>
                      <span className="font-head text-2xl leading-none">{ch.number}</span>
                      <span className="mt-0.5 text-[11px] font-bold uppercase leading-tight tracking-wide">{ch.name}</span>
                    </div>
                    <div className="guide-row-viewport flex-1 bg-[var(--bg-card)]">
                      <div className="guide-row-track" style={{ animationDuration: dur }}>
                        <RowGroup ch={ch} navigate={navigate} />
                        <RowGroup ch={ch} navigate={navigate} clone />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {(followItems.length > 0 || lovedItems.length > 0) && (
          <div className="mt-8 space-y-2 border-t border-[var(--border-subtle)] pt-4">
            {lovedItems.length > 0 && <PosterRow title="Loved by Friends" items={lovedItems} testid="row-loved_by_friends" />}
            {followItems.length > 0 && <PosterRow title="New From People You Follow" items={followItems} testid="row-following" />}
          </div>
        )}

        {indieItems.length > 0 && <PosterRow title="Indie Spotlight" items={indieItems} testid="row-indie_spotlight" seeAll="/indie" />}
        {teaser && <IndieTeaser />}
        <PodcastSpotlight />
      </main>
      <Disclaimer />
    </div>
  );
}

// Deterministic pseudo-progress (discovery app has no playback) — stable per title.
function progressPct(id) {
  let h = 0;
  for (const ch of String(id)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return 14 + (h % 74); // 14–87%
}

function RowGroup({ ch, navigate, clone = false }) {
  return (
    <div className="guide-row-group flex" {...(clone ? { "aria-hidden": true } : {})}>
      {ch.items.map((it) => (
        <button
          key={it.id}
          onClick={() => navigate(`/title/${it.id}`)}
          className="guide-title-cell group text-left"
          tabIndex={clone ? -1 : 0}
          {...(clone ? {} : { "data-testid": `guide-cell-${ch.service_id}-${it.id}` })}
        >
          <div className="aspect-[2/3] w-full overflow-hidden border border-[var(--border-subtle)] bg-[var(--bg-base)]">
            <img src={it.poster || it.img} alt={it.title} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-110" />
          </div>
          <p className="mt-0.5 line-clamp-2 text-[10px] font-bold leading-tight text-[var(--text-primary)]" title={it.title}>{it.title}</p>
          <p className="truncate text-[9px] text-[var(--text-secondary)]">{it.year || "—"} · ★{(it.rating || 0).toFixed(1)}</p>
        </button>
      ))}
    </div>
  );
}

function GuideStrip({ title, items, navigate, progress = false, testid, className = "" }) {
  if (!items || !items.length) return null;
  return (
    <section className={className} data-testid={testid}>
      <div className="mb-2 flex items-center gap-3">
        <span className="bg-[var(--accent)] px-3 py-1 font-head text-xl tracking-wide text-[var(--accent-contrast)]">{title}</span>
        <span className="h-px flex-1 bg-[var(--border-subtle)]" />
      </div>
      <div className="scrollbar-none flex gap-3 overflow-x-auto pb-2">
        {items.map((it) => {
          const pct = progress ? (typeof it.progress === "number" ? it.progress : progressPct(it.id)) : 0;
          return (
            <button key={it.id} onClick={() => navigate(`/title/${it.id}`)}
              className="group w-44 flex-none text-left" data-testid={`${testid}-item-${it.id}`}>
              <div className="relative aspect-video w-full overflow-hidden border-2 border-[var(--border-subtle)] bg-[var(--bg-card)] transition-colors group-hover:border-[var(--accent)]">
                <img src={it.img || it.poster} alt={it.title} loading="lazy" className="h-full w-full object-cover" />
                {progress && (
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/60" data-testid={`${testid}-progress-${it.id}`}>
                    <div className="h-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
              <p className="mt-1 truncate text-sm font-bold text-[var(--text-primary)]" title={it.title}>{it.title}</p>
              <p className="truncate text-[11px] text-[var(--text-secondary)]">{it.year} · ★{it.rating?.toFixed(1)}{progress ? ` · ${pct}% watched` : ""}</p>
              <ServiceBadges id={it.id} className="mt-1 w-fit" />
            </button>
          );
        })}
      </div>
    </section>
  );
}
